import { useEffect } from "react";
import { navigate } from "@reach/router";
import { useSelector } from "react-redux";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { selectCurrentUserId } from "newui/features/Auth/authSelectors";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useEvaluation } from "../../hooks/useEvaluation";
import { InitialEvaluationProvider } from "../../hooks/InitialEvaluationContext";
import { EVALUATION_STEP_NUMBERS } from "../../constants/evaluation.constants";
import { RfxLeadInfoBox } from "../steps/InitialEvaluationStep/panel/RfxLeadInfoBox";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { PanelMode } from "../../types/panel.type";
import { EvaluationModeSelector } from "./EvaluationModeSelector";
import { EvaluationAccessBoundary } from "../EvaluationAccessBoundary";
import { isSoloPanelMode } from "../../utils/panelModes";
import type { EvaluationState } from "../../types/evaluation.type";

interface EvaluationProgressSummaryProps {
	evaluationState: EvaluationState | null;
}

/** Read-only summary of evaluation type (solo/panel) and step progress, shown to all users. */
const EvaluationProgressSummary: React.FC<EvaluationProgressSummaryProps> = ({
	evaluationState,
}) => {
	const steps = evaluationState?.steps ?? [];
	const totalSteps = steps.length;
	const completedSteps = steps.filter((s) => s.status === "completed").length;
	const percent =
		totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
	const evalType = isSoloPanelMode(evaluationState?.panelMode ?? null)
		? "Solo"
		: "Panel";

	return (
		<div
			className="mb-4 rounded-lg border border-gray-200 bg-white p-4"
			data-testid="evaluation-progress-summary"
		>
			<div className="flex items-center justify-between">
				<span className="text-sm font-medium text-gray-900">
					Evaluation type
				</span>
				<span
					className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-700"
					data-testid="evaluation-type-badge"
				>
					{evalType}
				</span>
			</div>
			<div className="mt-3">
				<div className="flex items-center justify-between text-xs text-gray-500">
					<span>Progress</span>
					<span data-testid="evaluation-progress-label">
						{completedSteps} of {totalSteps} steps ({percent}%)
					</span>
				</div>
				<div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-100">
					<div
						className="h-full rounded-full bg-[#0031DD] transition-all"
						style={{ width: `${percent}%` }}
						data-testid="evaluation-progress-bar"
					/>
				</div>
			</div>
		</div>
	);
};

interface EvaluationTabDisplayProps {
	isClientView: boolean;
	rfxData: Rfx;
	rfxSubmissionId?: number;
}

export const EvaluationTabDisplay: React.FC<EvaluationTabDisplayProps> = ({
	rfxData,
	rfxSubmissionId,
	isClientView,
}) => {
	const { clientPaths } = useAuthContext();
	const hasAIAccess = useHasAIEvaluationAccess();
	const currentUserId = useSelector(selectCurrentUserId);
	const rfxId = rfxData?.id;
	const rfxLead = rfxData?.rfxLead;

	const {
		hasStarted,
		isLoading,
		evaluationState,
		currentUserContext,
		fetchEvaluationState,
		startEvaluation,
		changeMode,
	} = useEvaluation({ rfxId, rfxSubmissionId });

	// Pre-creation: compare current user with rfxLead.id
	// Post-creation: use the backend-derived context
	const isRfxLead = evaluationState
		? (currentUserContext?.isRfxLead ?? false)
		: (currentUserId !== null && rfxLead?.id === currentUserId);

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleView = (): void => {
		const evaluationId = evaluationState?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	const handleCreate = async (panelMode: PanelMode): Promise<void> => {
		if (!isRfxLead) return;
		const state = await startEvaluation(panelMode);
		const evaluationId = state?.id;
		if (evaluationId) {
			navigate(clientPaths.viewEvaluation(rfxId, evaluationId));
		}
	};

	// Compute whether mode switching is currently allowed
	const canChangeMode = (() => {
		if (!hasStarted || !evaluationState?.panelMode) return false;
		const mode = evaluationState.panelMode;
		const step = evaluationState.currentStep ?? 0;
		// Solo → Panel: allowed before Final Scoring (step number 5)
		if (mode === "manual_solo" || mode === "ai_solo") {
			return step < EVALUATION_STEP_NUMBERS.PANEL_MEETING;
		}
		// Panel → Solo: allowed before invites sent (for now, pass true; refine after testing)
		if (mode === "manual_panel" || mode === "ai_panel") {
			return true;
		}
		return false;
	})();

	const handleModeChange = async (newMode: PanelMode): Promise<void> => {
		await changeMode(newMode);
	};

	const handleAcceptPanelInvite = () => {
		navigate(clientPaths.viewEvaluation(rfxId, evaluationState?.id));
	};

	const handleDeclined = () => {
		fetchEvaluationState();
	};

	if (!isClientView) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	const header = (
		<div className="mb-6">
			<h2 className="text-xl font-semibold text-gray-900">
				RFx Evaluation Process
			</h2>
			<p className="mt-1 text-sm text-gray-500">
				{hasAIAccess
					? "Use AI-assisted evaluation to systematically score and compare supplier submissions."
					: "Systematically score and compare supplier submissions."}
			</p>
		</div>
	);

	// Non-lead member, evaluation started: a user without resolved access sees
	// only the access-denied prompt (the same screens as the evaluation page) —
	// nothing else. Members with access see the type/progress summary and the
	// View Evaluation button.
	const renderNonLeadStarted = (): React.ReactNode => (
		<EvaluationAccessBoundary
			access={currentUserContext?.access}
			rfxData={rfxData}
			evaluationState={evaluationState}
			currentUserContext={currentUserContext}
			onAccept={handleAcceptPanelInvite}
			onDecline={handleDeclined}
		>
			{header}
			<EvaluationProgressSummary evaluationState={evaluationState} />
			<Button
				btntype="primary"
				onClick={handleView}
				data-testid="view-evaluation-btn"
			>
				View Evaluation
			</Button>
		</EvaluationAccessBoundary>
	);

	return (
		<InitialEvaluationProvider rfxId={rfxId}>
			<div data-testid="evaluation-panel" className="min-h-96">
				{isRfxLead ? (
					<div>
						{header}
						{hasStarted && (
							<Button
								btntype="primary"
								onClick={handleView}
								data-testid="view-evaluation-btn"
								className="mb-4"
							>
								View Evaluation
							</Button>
						)}
						<EvaluationModeSelector
							onStart={handleCreate}
							hasAIAccess={hasAIAccess}
							isLoading={isLoading}
							currentMode={evaluationState?.panelMode as PanelMode | undefined}
							currentStep={evaluationState?.currentStep}
							canChangeMode={canChangeMode}
							onModeChange={handleModeChange}
						/>
					</div>
				) : !hasStarted ? (
					<div data-testid="evaluation-not-started-info">
						{header}
						<InstructionsInfoBox
							title="Evaluation not yet started"
							collapsible={false}
							instructions={[
								"Only the RFx Lead can start the evaluation process for this RFx.",
								"Once started, you will be notified if you are invited to participate as a panel member.",
								"Please contact the RFx Lead below if you have any questions.",
							]}
						/>
						<RfxLeadInfoBox
							rfxLead={rfxLead}
							marginTop="4"
						/>
					</div>
				) : (
					renderNonLeadStarted()
				)}
			</div>
		</InitialEvaluationProvider>
	);
};
